import { useEffect, useMemo, useRef, useState } from 'react';
import type { ZohoItem } from '../../types';
import { normalizeVendorName } from '../../lib/match-zoho-vendor';

interface Props {
  items: ZohoItem[];
  value: string | null;
  onChange: (itemId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Même combobox que VendorSelect, mais sur les items Zoho. On réutilise la
 * normalisation pour la recherche (insensible casse/espaces/diacritiques).
 */
export function ItemSelect({ items, value, onChange, placeholder, disabled }: Props) {
  const selected = useMemo(() => items.find((i) => i.id === value) ?? null, [items, value]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setQuery(selected?.name ?? '');
  }, [selected, open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery(selected?.name ?? '');
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open, selected]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items.slice(0, 200);
    const q = normalizeVendorName(query);
    return items
      .filter((i) => normalizeVendorName(i.name).includes(q))
      .slice(0, 50);
  }, [query, items]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  const choose = (i: ZohoItem | null) => {
    onChange(i?.id ?? null);
    setOpen(false);
    setQuery(i?.name ?? '');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filtered[highlight]) choose(filtered[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery(selected?.name ?? '');
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center gap-2">
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          value={open ? query : selected?.name ?? ''}
          onFocus={() => {
            setOpen(true);
            setQuery('');
          }}
          onChange={(e) => {
            setOpen(true);
            setQuery(e.target.value);
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder ?? 'Rechercher un produit Zoho…'}
          className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
        />
        {selected && !disabled && (
          <button
            type="button"
            onClick={() => choose(null)}
            className="text-slate-400 hover:text-slate-700 text-lg leading-none px-1"
            title="Effacer"
          >
            ×
          </button>
        )}
      </div>

      {open && !disabled && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-md bg-white shadow-lg ring-1 ring-slate-200 text-sm"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-slate-400 italic">Aucun produit trouvé.</li>
          ) : (
            filtered.map((i, idx) => (
              <li
                key={i.id}
                role="option"
                aria-selected={value === i.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(i);
                }}
                onMouseEnter={() => setHighlight(idx)}
                className={`px-3 py-1.5 cursor-pointer ${
                  idx === highlight ? 'bg-blue-50' : ''
                } ${value === i.id ? 'font-semibold' : ''}`}
              >
                <span>{i.name}</span>
                {i.account_name && (
                  <span className="text-xs text-slate-500 ml-2">· {i.account_name}</span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
